'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';
type AlertCategory = 'injury' | 'lineup' | 'goalie' | 'travel' | 'momentum' | 'model';

interface SentinelAlert {
  id: number;
  timestamp: string;
  severity: AlertSeverity;
  category: AlertCategory;
  title: string;
  detail: string;
  team: string;
  impact: string;
  projectionDelta: number;
  autoRecalibrated: boolean;
  confidence: number;
}

const ALERTS: SentinelAlert[] = [
  {
    id: 1, timestamp: '14:32', severity: 'critical', category: 'goalie',
    title: 'Gardien Confirmé — Changement Tardif',
    detail: 'Ilya Sorokin remplacé par Semyon Varlamov (blessure à l\'échauffement). Impact majeur sur les cotes.',
    team: 'NYI', impact: 'Cotes NYI +18 pts', projectionDelta: -12, autoRecalibrated: true, confidence: 94,
  },
  {
    id: 2, timestamp: '13:58', severity: 'high', category: 'injury',
    title: 'Blessure Joueur Clé Détectée',
    detail: 'Auston Matthews absent à l\'entraînement matinal. Statut: Questionable. Impact sur ligne 1 TOR.',
    team: 'TOR', impact: 'Projections TOR -8%', projectionDelta: -8, autoRecalibrated: true, confidence: 87,
  },
  {
    id: 3, timestamp: '13:15', severity: 'high', category: 'lineup',
    title: 'Changement Alignement Majeur',
    detail: 'Colorado repositionne MacKinnon en ligne 2 pour matchup favorable. Stratégie tactique détectée.',
    team: 'COL', impact: 'EV MacKinnon +15%', projectionDelta: 15, autoRecalibrated: true, confidence: 82,
  },
  {
    id: 4, timestamp: '12:44', severity: 'medium', category: 'travel',
    title: 'Fatigue Voyage Détectée',
    detail: 'Edmonton — 3ème match en 4 jours, vol de nuit depuis Vancouver. Indice fatigue: 78/100.',
    team: 'EDM', impact: 'Performance -5%', projectionDelta: -5, autoRecalibrated: true, confidence: 76,
  },
  {
    id: 5, timestamp: '12:10', severity: 'medium', category: 'momentum',
    title: 'Momentum Shift Détecté',
    detail: 'Boston — 6 victoires consécutives, xGF/60 en hausse de 22% sur 10 derniers matchs.',
    team: 'BOS', impact: 'Confiance +12%', projectionDelta: 12, autoRecalibrated: false, confidence: 88,
  },
  {
    id: 6, timestamp: '11:30', severity: 'low', category: 'model',
    title: 'Recalibration Modèle Automatique',
    detail: 'Modèle xG recalibré suite à 3 anomalies détectées. RMSE amélioré de 0.08 → 0.06.',
    team: 'SYS', impact: 'Précision +4%', projectionDelta: 4, autoRecalibrated: true, confidence: 99,
  },
];

const SEVERITY_CONFIG: Record<AlertSeverity, { color: string; bg: string; border: string; label: string }> = {
  critical: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/40', label: 'CRITIQUE' },
  high: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/40', label: 'ÉLEVÉ' },
  medium: { color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/40', label: 'MOYEN' },
  low: { color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/40', label: 'INFO' },
};

const CATEGORY_ICONS: Record<AlertCategory, string> = {
  injury: 'ExclamationTriangleIcon',
  lineup: 'UsersIcon',
  goalie: 'ShieldCheckIcon',
  travel: 'TruckIcon',
  momentum: 'ArrowTrendingUpIcon',
  model: 'CpuChipIcon',
};

const CATEGORY_LABELS: Record<AlertCategory, string> = {
  injury: 'Blessure', lineup: 'Alignement', goalie: 'Gardien',
  travel: 'Voyage', momentum: 'Momentum', model: 'Modèle IA',
};

export default function AISentinelClient() {
  const [filter, setFilter] = useState<AlertSeverity | 'all'>('all');
  const [monitoring, setMonitoring] = useState(true);
  const [scanCount, setScanCount] = useState(1247);
  const [lastScan, setLastScan] = useState('14:32:18');

  useEffect(() => {
    if (!monitoring) return;
    const interval = setInterval(() => {
      setScanCount(c => c + Math.floor(Math.random() * 3) + 1);
      const now = new Date();
      setLastScan(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`);
    }, 3000);
    return () => clearInterval(interval);
  }, [monitoring]);

  const filtered = ALERTS.filter(a => filter === 'all' || a.severity === filter);
  const criticalCount = ALERTS.filter(a => a.severity === 'critical').length;
  const highCount = ALERTS.filter(a => a.severity === 'high').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 flex items-center justify-center">
            <Icon name="ShieldCheckIcon" size={20} className="text-red-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">AI Sentinel</h1>
            <p className="text-xs text-muted-foreground font-mono-data">Surveillance Autonome · Auto-Recalibration · NHL 2025-26</p>
          </div>
        </div>
        <button
          onClick={() => setMonitoring(m => !m)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${monitoring ? 'bg-positive-subtle border-positive/30 text-positive' : 'bg-card border-border text-muted-foreground'}`}
        >
          <span className={`w-2 h-2 rounded-full ${monitoring ? 'bg-positive pulse-dot' : 'bg-muted-foreground'}`} />
          {monitoring ? 'Surveillance Active' : 'Surveillance Pausée'}
        </button>
      </div>

      {/* Status bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Scans Effectués', value: scanCount.toLocaleString(), icon: 'MagnifyingGlassIcon', color: 'text-primary' },
          { label: 'Dernier Scan', value: lastScan, icon: 'ClockIcon', color: 'text-foreground' },
          { label: 'Alertes Critiques', value: criticalCount, icon: 'ExclamationTriangleIcon', color: 'text-red-400' },
          { label: 'Auto-Recalibrés', value: ALERTS.filter(a => a.autoRecalibrated).length, icon: 'ArrowPathIcon', color: 'text-positive' },
        ].map((s, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={14} className={s.color} />
              <span className="text-2xs text-muted-foreground font-mono-data">{s.label}</span>
            </div>
            <div className={`text-xl font-bold font-mono-data ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Monitoring scope */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="EyeIcon" size={14} className="text-primary" />
          Périmètre de Surveillance
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            { label: 'Blessures', icon: 'ExclamationTriangleIcon', active: true },
            { label: 'Alignements', icon: 'UsersIcon', active: true },
            { label: 'Gardiens', icon: 'ShieldCheckIcon', active: true },
            { label: 'Voyages', icon: 'TruckIcon', active: true },
            { label: 'Momentum', icon: 'ArrowTrendingUpIcon', active: true },
            { label: 'Modèles IA', icon: 'CpuChipIcon', active: true },
          ].map((item, i) => (
            <div key={i} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border ${item.active ? 'bg-positive-subtle border-positive/30' : 'bg-card border-border'}`}>
              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} className={item.active ? 'text-positive' : 'text-muted-foreground'} />
              <span className="text-2xs font-medium text-center">{item.label}</span>
              {item.active && <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />}
            </div>
          ))}
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {(['all', 'critical', 'high', 'medium', 'low'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}>
            {f === 'all' ? 'Toutes' : SEVERITY_CONFIG[f as AlertSeverity]?.label}
            {f !== 'all' && <span className="ml-1.5 opacity-70">{ALERTS.filter(a => a.severity === f).length}</span>}
          </button>
        ))}
      </div>

      {/* Alerts */}
      <div className="space-y-3">
        {filtered.map(alert => {
          const sev = SEVERITY_CONFIG[alert.severity];
          return (
            <div key={alert.id} className={`rounded-xl border p-4 ${sev.bg} ${sev.border}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sev.bg} border ${sev.border}`}>
                    <Icon name={CATEGORY_ICONS[alert.category] as Parameters<typeof Icon>[0]['name']} size={16} className={sev.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold text-foreground">{alert.title}</span>
                      <span className={`text-2xs px-1.5 py-0.5 rounded font-mono-data ${sev.bg} ${sev.color} border ${sev.border}`}>{sev.label}</span>
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-card border border-border text-muted-foreground">{CATEGORY_LABELS[alert.category]}</span>
                      {alert.autoRecalibrated && (
                        <span className="text-2xs px-1.5 py-0.5 rounded bg-positive-subtle border border-positive/30 text-positive font-mono-data">AUTO-RECALIBRÉ</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{alert.detail}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-xs font-mono-data text-foreground">Équipe: <span className="text-primary">{alert.team}</span></span>
                      <span className="text-xs font-mono-data text-foreground">Impact: <span className={alert.projectionDelta > 0 ? 'text-positive' : 'text-negative'}>{alert.projectionDelta > 0 ? '+' : ''}{alert.projectionDelta}%</span></span>
                      <span className="text-xs font-mono-data text-foreground">Confiance: <span className="text-primary">{alert.confidence}%</span></span>
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-mono-data text-muted-foreground">{alert.timestamp}</div>
                  <div className="text-xs text-muted-foreground mt-1">{alert.impact}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
